/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "How Identity Resolution works", description = "Learn how Hightouch builds identity graphs, applies deterministic matching, and produces stable resolved identities.", lastUpdatedDate = new Date(1789430400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "How Identity Resolution works",
        description: "Learn how Hightouch builds identity graphs, applies deterministic matching, and produces stable resolved identities.",
        lastUpdatedDate: "Tue Sep 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Identity graphs",
          "depth": 0,
          "id": "identity-graphs"
        }, {
          "value": "Deterministic matching",
          "depth": 0,
          "id": "deterministic-matching"
        }, {
          "value": "Identifiers and merge priority",
          "depth": 0,
          "id": "identifiers-and-merge-priority"
        }, {
          "value": "Identifier limits and over-merge protection",
          "depth": 0,
          "id": "identifier-limits-and-over-merge-protection"
        }, {
          "value": "Incremental runs and identity stability",
          "depth": 0,
          "id": "incremental-runs-and-identity-stability"
        }, {
          "value": "Full re-runs",
          "depth": 1,
          "id": "full-re-runs"
        }, {
          "value": "From resolved identities to profiles",
          "depth": 0,
          "id": "from-resolved-identities-to-profiles"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      code: "code",
      ul: "ul",
      li: "li",
      a: "a",
      img: "img",
      blockquote: "blockquote",
      h3: "h3"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Identity resolution",
        extraText: ". You can use it with or without Customer Studio."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data engineers, analytics engineers, and technical marketers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "An Identity Resolution project"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Understand how Identity Resolution groups records, applies matching rules, and maintains identity stability over time."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Identity Resolution groups input records that refer to the same real-world entity into ", _jsx(_components.strong, {
          children: "resolved identities"
        }), ". It does this by building and maintaining an ", _jsx(_components.strong, {
          children: "identity graph"
        }), " based on the identifiers and rules you configure."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each resolved identity is represented by a synthetic ", _jsx(_components.code, {
          children: "ht_id"
        }), "."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ht_id"
          }), " values carry over across incremental runs and full re-runs."]
        }), "\n", _jsxs(_components.li, {
          children: ["A profile's ", _jsx(_components.code, {
            children: "ht_id"
          }), " changes only when its records change, most often because two profiles merge or one splits. A merge collapses two ", _jsx(_components.code, {
            children: "ht_id"
          }), " values into one; a split turns one into two."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This page explains how Identity Resolution behaves conceptually, independent of any specific UI or setup steps."
      }), "\n", _jsxs(Info, {
        children: [_jsx(_components.p, {
          children: _jsx(_components.a, {
            href: "https://architecture.hightouch.com/?from=docs&product=identity-resolution&utm_source=hightouch-docs&utm_medium=referral&utm_campaign=architecture-explorer&utm_content=identity-resolution",
            children: _jsx(_components.strong, {
              children: "Explore the interactive architecture diagram →"
            })
          })
        }), _jsx(_components.p, {
          children: "See how data flows and where Identity Resolution fits in your stack."
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "identity-graphs",
        children: "Identity graphs"
      }), "\n", _jsxs(_components.p, {
        children: ["An ", _jsx(_components.strong, {
          children: "identity graph"
        }), " connects records through shared identifiers."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Input records act as ", _jsx(_components.strong, {
            children: "nodes"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Identifiers create ", _jsx(_components.strong, {
            children: "edges"
          }), " between records"]
        }), "\n", _jsxs(_components.li, {
          children: ["Connected groups of records form ", _jsx(_components.strong, {
            children: "resolved identities"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If two records share a matching identifier (for example, the same ", _jsx(_components.code, {
          children: "email"
        }), "), they are connected in the graph. If no valid connection exists, they remain part of separate identities."]
      }), "\n", _jsx(_components.p, {
        children: "How records connect—and which connections are allowed—is entirely determined by your configured identifiers and rules."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/identity-resolution/identity-graph/idr-identity-graph.png",
          alt: "Identity graph summary"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "deterministic-matching",
        children: "Deterministic matching"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Identity Resolution uses ", _jsx(_components.strong, {
          children: "deterministic matching"
        }), ", which relies on exact identifier equality."]
      }), "\n", _jsx(_components.p, {
        children: "With deterministic matching:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Identifier values must ", _jsx(_components.strong, {
            children: "match exactly"
          }), " to connect records"]
        }), "\n", _jsxs(_components.li, {
          children: ["Matching behavior is ", _jsx(_components.strong, {
            children: "predictable and explainable"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Re-running the graph with the same data and rules produces the same groupings (excluding full re-runs or rule changes)"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Deterministic matching provides a stable foundation for analytics, activation, and operational workflows where correctness and auditability matter."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "identifiers-and-merge-priority",
        children: "Identifiers and merge priority"
      }), "\n", _jsxs(_components.p, {
        children: ["Identifiers define ", _jsx(_components.em, {
          children: "how"
        }), " records are allowed to connect in the identity graph."]
      }), "\n", _jsx(_components.p, {
        children: "For each identifier, you configure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Which ", _jsx(_components.strong, {
            children: "column"
          }), " contains the identifier value"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "priority order"
          }), " relative to other identifiers"]
        }), "\n", _jsxs(_components.li, {
          children: ["Optional ", _jsx(_components.strong, {
            children: "limits"
          }), " on how many distinct values an identity can contain"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When a record contains multiple identifiers, Identity Resolution evaluates them in ", _jsx(_components.strong, {
          children: "priority order"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Higher-priority identifiers are applied first and can prevent lower-priority identifiers from creating unintended merges. This ordering controls how aggressively records are linked as new data arrives."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "identity-resolution/idr-identifier-rules-priority.png",
          alt: "Merge rules"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "identifier-limits-and-over-merge-protection",
        children: "Identifier limits and over-merge protection"
      }), "\n", _jsx(_components.p, {
        children: "Identifier limits prevent identities from growing too large due to shared, reused, or low-quality identifiers."
      }), "\n", _jsx(_components.p, {
        children: "A limit defines a rule such as:"
      }), "\n", _jsxs(_components.blockquote, {
        children: ["\n", _jsxs(_components.p, {
          children: ["“An identity may contain at most ", _jsx(_components.strong, {
            children: "N distinct values"
          }), " of this identifier type.”"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When a merge would violate a limit, Identity Resolution uses both ", _jsx(_components.strong, {
          children: "priority"
        }), " and ", _jsx(_components.strong, {
          children: "limits"
        }), " to determine the outcome:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: _jsx(_components.strong, {
              children: "Higher-priority identifier exceeds its limit"
            })
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "The merge is blocked, and the record remains part of a separate identity."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: _jsx(_components.strong, {
              children: "Lower-priority identifier exceeds its limit"
            })
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["The identity retains only the first ", _jsx(_components.strong, {
                children: "N"
              }), " values."]
            }), "\n", _jsx(_components.li, {
              children: "Additional values are ignored for matching purposes."
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This behavior prevents unrelated records from collapsing into a single identity while still allowing high-confidence identifiers to drive resolution."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "identity-resolution/idr-limit-values.png",
          alt: "Limit rules"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "incremental-runs-and-identity-stability",
        children: "Incremental runs and identity stability"
      }), "\n", _jsxs(_components.p, {
        children: ["Every record gets its own ", _jsx(_components.code, {
          children: "ht_id"
        }), " when it is first ingested. A ", _jsx(_components.em, {
          children: "profile's"
        }), " ", _jsx(_components.code, {
          children: "ht_id"
        }), " is the lowest ", _jsx(_components.code, {
          children: "ht_id"
        }), " among its records, and it keeps that ", _jsx(_components.code, {
          children: "ht_id"
        }), " for as long as those records stay together."]
      }), "\n", _jsxs(_components.p, {
        children: ["Identity Resolution processes data ", _jsx(_components.strong, {
          children: "incrementally"
        }), " as new or updated records arrive. Each input model's timestamp column determines which rows are net new and need to be processed."]
      }), "\n", _jsx(_components.p, {
        children: "During incremental runs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Existing identities typically retain their ", _jsx(_components.code, {
            children: "ht_id"
          })]
        }), "\n", _jsx(_components.li, {
          children: "New records attach to existing identities when matches are found"
        }), "\n", _jsx(_components.li, {
          children: "New identities are created when no valid matches exist"
        }), "\n", _jsx(_components.li, {
          children: "Previously separate identities may merge if new data connects them"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When a merge happens, the lower-numbered ", _jsx(_components.code, {
          children: "ht_id"
        }), " absorbs the higher-numbered one. For example, if new data links ", _jsx(_components.code, {
          children: "ht10"
        }), " and ", _jsx(_components.code, {
          children: "ht14"
        }), ", the two identities collapse into ", _jsx(_components.code, {
          children: "ht10"
        }), " and ", _jsx(_components.code, {
          children: "ht14"
        }), " is dropped. The dropped ", _jsx(_components.code, {
          children: "ht_id"
        }), " is no longer present in the next run's output, but all of its records appear on the surviving identity. This is expected behavior — it reflects that Identity Resolution has learned the two profiles refer to the same entity."]
      }), "\n", _jsx(_components.h3, {
        id: "full-re-runs",
        children: "Full re-runs"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "full re-run"
        }), " recomputes the graph from an empty state. This happens when:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "You make an identifier limit rule stricter — for example, lowering the threshold on an existing limit, or adding a new limit rule"
        }), "\n", _jsxs(_components.li, {
          children: ["A large amount of bad data requires resetting the graph. Smaller corrections can usually be handled with the ", _jsx(_components.a, {
            href: "/identity-resolution/profile-reprocessing",
            children: "profile reprocessing API"
          }), " instead, which avoids a full re-run"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After a full re-run, each record is matched back to its original ", _jsx(_components.code, {
          children: "ht_id"
        }), ", so a profile that re-forms with the same records keeps the same ", _jsx(_components.code, {
          children: "ht_id"
        }), ". In practice nearly every profile does."]
      }), "\n", _jsxs(_components.p, {
        children: ["A full re-run recomputes profiles from scratch, so it is the only time, other than ", _jsx(_components.a, {
          href: "/identity-resolution/profile-reprocessing",
          children: "reprocessing"
        }), ", that a profile can split. When one does, because of a stricter limit rule, a blocklisted identifier, or deleted source data, the ", _jsx(_components.code, {
          children: "ht_id"
        }), " stays with the record that first carried it. New ", _jsx(_components.code, {
          children: "ht_id"
        }), " values are always higher than any issued before, so a dropped id is never reassigned to a different profile."]
      }), "\n", _jsxs(_components.p, {
        children: ["For activation guidance, see ", _jsx(_components.a, {
          href: "/identity-resolution/usage#using-ht_id-as-a-sync-key",
          children: "Using ht_id as a sync key"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "from-resolved-identities-to-profiles",
        children: "From resolved identities to profiles"
      }), "\n", _jsxs(_components.p, {
        children: ["Identity Resolution produces ", _jsx(_components.strong, {
          children: "resolved identities"
        }), " represented by ", _jsx(_components.code, {
          children: "ht_id"
        }), ". How those identities are exposed downstream depends on how you consume the outputs."]
      }), "\n", _jsx(_components.p, {
        children: "Common patterns include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: _jsx(_components.strong, {
              children: "Golden Record"
            })
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "A flattened, one-row-per-identity table"
            }), "\n", _jsx(_components.li, {
              children: "Uses survivorship rules (recency, frequency, source priority, arrays) to select canonical values"
            }), "\n", _jsx(_components.li, {
              children: "Often used for analytics, activation, and Customer 360 use cases"
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: _jsx(_components.strong, {
              children: "Customer Studio"
            })
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Uses Golden Record as the ", _jsx(_components.strong, {
                children: "parent model"
              })]
            }), "\n", _jsx(_components.li, {
              children: "Allows non-technical users to define traits, audiences, and journeys"
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["A resolved identity exists independently of Golden Record. Golden Record and Customer Studio define how identities are ", _jsx(_components.strong, {
          children: "represented and consumed"
        }), ", not how they are resolved."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/identity-resolution/golden-record/idr-golden-record-profiles.png",
          alt: "Golden Record profiles"
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
